export type SeoMetadata = {
  author?: string;
  canonicalUrl: string;
  description: string;
  imageAlt?: string;
  imageHeight?: number;
  imageUrl?: string;
  imageWidth?: number;
  keywords?: string[];
  locale?: string;
  publishedAt?: string;
  robots?: string;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
  tags?: string[];
  title: string;
  type?: 'article' | 'website';
  updatedAt?: string;
};

type MetaAttribute = 'name' | 'property';

export const siteName = 'Functor';
export const defaultSeoTitle = 'Functor | AI, Data & Analytics Consultancy';
export const defaultSeoDescription =
  'Functor designs, builds, and maintains modern data platforms and enterprise AI systems for reliable analytics, automation, and scalable infrastructure.';
export const openGraphLocaleByLanguage = {
  en: 'en_US',
  sr: 'sr_RS',
  es: 'es_ES'
} as const;

const defaultSiteUrl = 'https://functor.dev';
const managedStructuredDataId = 'seo-structured-data';

const getEnvValue = (value: string | boolean | undefined) =>
  typeof value === 'string' ? value.trim() : '';

export const getSiteUrl = () => {
  const configuredSiteUrl = getEnvValue(import.meta.env.VITE_SITE_URL);
  const siteUrl = configuredSiteUrl || defaultSiteUrl;

  return siteUrl.replace(/\/+$/, '');
};

export const createAbsoluteUrl = (
  urlOrPath: string | undefined,
  baseUrl = getSiteUrl()
) => {
  const value = urlOrPath?.trim();

  if (!value) return '';

  try {
    return new URL(value, baseUrl).toString();
  } catch {
    return '';
  }
};

const getHeadElement = () =>
  typeof document === 'undefined' ? null : document.head;

const normalizeContent = (content: string | number | undefined) => {
  if (content === undefined) return '';

  return String(content).trim();
};

const findMetaElements = (attribute: MetaAttribute, key: string) => {
  const head = getHeadElement();

  if (!head) return [];

  return Array.from(head.querySelectorAll('meta')).filter(
    meta => meta.getAttribute(attribute) === key
  );
};

const removeMeta = (attribute: MetaAttribute, key: string) => {
  findMetaElements(attribute, key).forEach(meta => meta.remove());
};

const setMeta = (
  attribute: MetaAttribute,
  key: string,
  content: string | number | undefined
) => {
  const normalizedContent = normalizeContent(content);
  const matchingMetaElements = findMetaElements(attribute, key);
  const metaElement = matchingMetaElements[0] ?? document.createElement('meta');

  matchingMetaElements.slice(1).forEach(meta => meta.remove());

  if (!normalizedContent) {
    metaElement.remove();
    return;
  }

  metaElement.setAttribute(attribute, key);
  metaElement.setAttribute('content', normalizedContent);
  metaElement.setAttribute('data-seo-managed', 'true');

  if (!metaElement.parentElement) {
    document.head.appendChild(metaElement);
  }
};

const setRepeatedMeta = (
  attribute: MetaAttribute,
  key: string,
  values: string[] | undefined
) => {
  removeMeta(attribute, key);

  const normalizedValues = Array.from(
    new Set((values ?? []).map(value => value.trim()).filter(Boolean))
  );

  normalizedValues.forEach(value => {
    const metaElement = document.createElement('meta');
    metaElement.setAttribute(attribute, key);
    metaElement.setAttribute('content', value);
    metaElement.setAttribute('data-seo-managed', 'true');
    document.head.appendChild(metaElement);
  });
};

const setCanonicalUrl = (canonicalUrl: string) => {
  const head = getHeadElement();

  if (!head) return;

  const normalizedUrl = canonicalUrl.trim();
  const matchingLinks = Array.from(head.querySelectorAll('link')).filter(
    link => link.getAttribute('rel') === 'canonical'
  );
  const linkElement = matchingLinks[0] ?? document.createElement('link');

  matchingLinks.slice(1).forEach(link => link.remove());

  if (!normalizedUrl) {
    linkElement.remove();
    return;
  }

  linkElement.setAttribute('rel', 'canonical');
  linkElement.setAttribute('href', normalizedUrl);
  linkElement.setAttribute('data-seo-managed', 'true');

  if (!linkElement.parentElement) {
    head.appendChild(linkElement);
  }
};

const setStructuredData = (
  structuredData: SeoMetadata['structuredData'] | undefined
) => {
  const head = getHeadElement();

  if (!head) return;

  const existingScript = document.getElementById(
    managedStructuredDataId
  ) as HTMLScriptElement | null;

  if (!structuredData) {
    existingScript?.remove();
    return;
  }

  const scriptElement = existingScript ?? document.createElement('script');

  scriptElement.id = managedStructuredDataId;
  scriptElement.type = 'application/ld+json';
  scriptElement.textContent = JSON.stringify(structuredData);

  if (!scriptElement.parentElement) {
    head.appendChild(scriptElement);
  }
};

const normalizeKeywords = (keywords: string[] | undefined) =>
  Array.from(
    new Set((keywords ?? []).map(keyword => keyword.trim()).filter(Boolean))
  );

const removeLegacyTwitterPropertyTags = () => {
  [
    'twitter:card',
    'twitter:url',
    'twitter:title',
    'twitter:description',
    'twitter:image',
    'twitter:image:alt'
  ].forEach(key => removeMeta('property', key));
};

export const applySeoMetadata = (metadata: SeoMetadata) => {
  if (typeof document === 'undefined') return;

  const title = metadata.title.trim() || defaultSeoTitle;
  const description = metadata.description.trim() || defaultSeoDescription;
  const canonicalUrl = metadata.canonicalUrl.trim() || getSiteUrl();
  const imageUrl = metadata.imageUrl?.trim();
  const imageAlt = metadata.imageAlt?.trim() || title;
  const type = metadata.type ?? 'website';
  const author = metadata.author?.trim() || siteName;
  const robots = metadata.robots?.trim() || 'index, follow';
  const keywords = normalizeKeywords(metadata.keywords);

  removeLegacyTwitterPropertyTags();

  document.title = title;

  setMeta('name', 'title', title);
  setMeta('name', 'description', description);
  setMeta('name', 'robots', robots);
  setMeta('name', 'author', author);
  setMeta('name', 'keywords', keywords.join(', '));

  setCanonicalUrl(canonicalUrl);

  setMeta('property', 'og:type', type);
  setMeta('property', 'og:site_name', siteName);
  setMeta('property', 'og:url', canonicalUrl);
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:image', imageUrl);
  setMeta('property', 'og:image:secure_url', imageUrl);
  setMeta('property', 'og:image:alt', imageUrl ? imageAlt : undefined);
  setMeta(
    'property',
    'og:image:width',
    imageUrl ? metadata.imageWidth : undefined
  );
  setMeta(
    'property',
    'og:image:height',
    imageUrl ? metadata.imageHeight : undefined
  );
  setMeta('property', 'og:locale', metadata.locale);

  setMeta('name', 'twitter:card', imageUrl ? 'summary_large_image' : 'summary');
  setMeta('name', 'twitter:url', canonicalUrl);
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image', imageUrl);
  setMeta('name', 'twitter:image:alt', imageUrl ? imageAlt : undefined);

  if (type === 'article') {
    setMeta('property', 'article:published_time', metadata.publishedAt);
    setMeta('property', 'article:modified_time', metadata.updatedAt);
    setMeta('property', 'article:author', author);
    setRepeatedMeta('property', 'article:tag', metadata.tags);
  } else {
    removeMeta('property', 'article:published_time');
    removeMeta('property', 'article:modified_time');
    removeMeta('property', 'article:author');
    removeMeta('property', 'article:tag');
  }

  setStructuredData(metadata.structuredData);
};
